import React, { ReactElement, useEffect } from 'react';
import { BoardContext } from 'store/contexts/board';
import { BlockContext, useBlockStateSelector } from 'store/contexts/block';
import { useContextSelector } from 'use-context-selector';
import { GetAllBlocksAPIResponse } from 'store/types/block';
import { fetchAllBlocks } from 'utils/requests';
import styled from 'styled-components';
import Grid from 'components/Board/Grid';
import { toast } from 'react-toastify';
import ClipLoader from 'react-spinners/ClipLoader';

import FlipHorizontallyIcon from 'assets/icons/flip_horizontally.png';
import FlipVerticallyIcon from 'assets/icons/flip_vertically.png';
import RotateClockwiseIcon from 'assets/icons/rotate_cw.png';
import RotateCounterClockwiseIcon from 'assets/icons/rotate_ccw.png';

import {
  rotateGridClockwise,
  rotateGridCounterClockwise,
  flipGridHorizontally,
  flipGridVertically,
} from 'utils/grid';

interface Props {
  header: string;
}

const BlockList = ({ header }: Props): ReactElement => {
  const setBoardState = useContextSelector(
    BoardContext,
    context => context.setState,
  );

  const blockState = useBlockStateSelector();
  const setBlockState = useContextSelector(
    BlockContext,
    context => context.setState,
  );

  const transformBlock = (
    index: number,
    transformedBlock: boolean[][],
  ): void => {
    setBlockState(state => ({
      ...state,
      data: state.data.map((block, dataIndex) => {
        if (dataIndex === index) {
          return { ...block, shape: transformedBlock };
        } else {
          return block;
        }
      }),
    }));
  };

  const onSeedSelect = (index: number): void => {
    if (blockState.selectedBlockIndex === index) {
      setBoardState(state => ({
        ...state,
        isPlacingSeed: false,
      }));
      setBlockState(state => ({
        ...state,
        selectedBlockIndex: null,
      }));
      return;
    }
    setBoardState(state => ({
      ...state,
      isPlacingSeed: true,
      isEditingGrid: true,
    }));
    setBlockState(state => ({
      ...state,
      selectedBlockIndex: index,
    }));
  };

  const onSeedQuery = (query: string): void => {
    const filteredBlocks = blockState.masterData.filter(data =>
      data.name.toLowerCase().includes(query.toLowerCase()),
    );
    setBoardState(state => ({
      ...state,
      isPlacingSeed: false,
    }));
    setBlockState(state => ({
      ...state,
      data: filteredBlocks,
      selectedBlockIndex: null,
    }));
  };

  useEffect(() => {
    setBlockState(state => ({ ...state, isLoading: true }));
    fetchAllBlocks()
      .then(res => {
        const response = res as GetAllBlocksAPIResponse;
        setBlockState(state => ({
          ...state,
          data: response.data,
          masterData: response.data,
          isLoading: false,
        }));
      })
      .catch(error => {
        toast.error(String(error));
        setBlockState(state => ({
          ...state,
          data: [],
          masterData: [],
          isLoading: false,
        }));
      });
  }, [setBlockState, blockState.isSubmitLoading]);

  if (blockState.isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minWidth: '30vw',
        }}
      >
        <ClipLoader />
      </div>
    );
  }

  return (
    <Layout>
      <SelectionHeader>
        {header}
        <StyledInput
          type="text"
          name="seedQuery"
          placeholder="Search a block pattern"
          onChange={(e): void => onSeedQuery(e.target.value)}
        />
      </SelectionHeader>
      {blockState.data.length ? (
        blockState.data.map(
          (data, index): ReactElement => (
            <BlockContainer
              key={index}
              onClick={(): void => onSeedSelect(index)}
              style={{
                backgroundColor:
                  index === blockState.selectedBlockIndex
                    ? 'lavender'
                    : 'transparent',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'flex-start' }}>
                {data.name}
                {data.is_stable && <Badge>STABLE</Badge>}
                <Icon
                  src={RotateClockwiseIcon}
                  onClick={(): void =>
                    transformBlock(
                      index,
                      rotateGridClockwise(blockState.data[index].shape),
                    )
                  }
                  alt="Rotate Block Clockwise"
                  title="Rotate Block Clockwise"
                />
                <Icon
                  src={RotateCounterClockwiseIcon}
                  onClick={(): void =>
                    transformBlock(
                      index,
                      rotateGridCounterClockwise(blockState.data[index].shape),
                    )
                  }
                  alt="Rotate Block Counter Clockwise"
                  title="Rotate Block Counter Clockwise"
                />
                <Icon
                  src={FlipHorizontallyIcon}
                  onClick={(): void =>
                    transformBlock(
                      index,
                      flipGridHorizontally(blockState.data[index].shape),
                    )
                  }
                  alt="Flip Block Horizontally"
                  title="Flip Block Horizontally"
                />
                <Icon
                  src={FlipVerticallyIcon}
                  onClick={(): void =>
                    transformBlock(
                      index,
                      flipGridVertically(blockState.data[index].shape),
                    )
                  }
                  alt="Flip Block Vertically"
                  title="Flip Block Vertically"
                />
              </label>
              <DescriptionWrapper>{data.description}</DescriptionWrapper>
              <Grid grid={data.shape} cellSize={9} />
            </BlockContainer>
          ),
        )
      ) : (
        <Info>No block pattern found</Info>
      )}
    </Layout>
  );
};

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  scrollbar-width: '-5px'; /* Firefox */
  overflow-x: hidden;
  max-height: 87.5vh;
  min-width: 31vw;
  padding: 0 1em;

  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: white;
  }

  &::-webkit-scrollbar {
    width: 8px;
    background-color: white;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: lightgray;
  }
`;

const BlockContainer = styled.div`
  border-top: 0.5px solid black;
  border-collapse: collapse;
  padding: 0.5em;
  display: flex;
  flex-direction: column;
  cursor: pointer;
`;

const DescriptionWrapper = styled.label`
  font-size: 0.75em;
  color: grey;
  margin-bottom: 0.5em;
  cursor: pointer;
`;

const SelectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5em;
  font-size: 1.25em;
  text-align: center;
  position: sticky;
  top: 0;
  background-color: white;
`;

const Info = styled.div`
  margin: 2em 0;
  font-size: 1em;
  text-align: center;
`;

const Badge = styled.span`
  background-color: lavender;
  font-color: white;
  font-size: 0.5em;
  padding: 0.25em;
  height: 1em;
  margin-left: 1em;
`;

const Icon = styled.img`
  width: 10px;
  height: 10px;
  margin: 2px 5px 2px 5px;
`;

const StyledInput = styled.input`
  text-align: center;
  border: 1px solid grey;
  border-radius: 4px;
  height: 1.75em;
  width: 20em;
  padding: 0.25em 0.5em;
  margin: 0.5em;
`;

export default BlockList;
